import { useAbsenceApplications } from '@/components/AbsenceApplication/hooks/useAbsenceApplications';
import type { AbsenceApplication } from '@/components/AbsenceApplication/types';
import { useAbsenceApplication } from '@/components/AbsenceApplication/useAbsenceApplication';
import { useUserContext } from '@/auth/UserContext';
import { useAbsenceExcuses } from '@/pages/Overview/hooks/useAbsenceExcuses';
import {
  useAttendanceStatistics,
  type DisciplineTypeStatistics,
} from '@/pages/Overview/hooks/useAttendanceStatistics';
import {
  DisciplineType,
  KNOWN_DISCIPLINE_TYPES,
  type StudyYearPeriod,
} from '@/types/overview';
import { PARENT_ROLES } from '@/types/user';

const EMPTY_MANDATORY_CONTENT: DisciplineTypeStatistics = {
  type: DisciplineType.MandatoryContent,
  attendableLessonsCount: 0,
  total: 0,
  justifiedCount: 0,
  sicknessCount: 0,
  otherCount: 0,
  nonJustifiedCount: 0,
};

const sortByFromAsc = (
  first: AbsenceApplication,
  second: AbsenceApplication,
): number =>
  new Date(first.absencePeriod.from).getTime() -
  new Date(second.absencePeriod.from).getTime();

export const useAttendance = (selectedPeriod: StudyYearPeriod) => {
  const { user } = useUserContext();
  const {
    onOpen: openAbsenceApplication,
    onOpenList: openAbsenceHistory,
    onOpenDetail,
  } = useAbsenceApplication();

  const canReportAbsence =
    !!user?.personType && PARENT_ROLES.has(user.personType);

  const { data: applications, isLoading: absencesAreLoading } =
    useAbsenceApplications({ enabled: canReportAbsence });
  const { data: statistics, isFetching: isFetchingStatistics } =
    useAttendanceStatistics(selectedPeriod);
  const { data: excuses } = useAbsenceExcuses(selectedPeriod);

  const isLoading = absencesAreLoading || !statistics || isFetchingStatistics;
  const activeApplications = (applications ?? [])
    .filter((application) => !application.isExpired)
    .sort(sortByFromAsc);

  const disciplineTypes = statistics?.disciplineTypes ?? [];
  const isMandatoryContent = (discipline: DisciplineTypeStatistics): boolean =>
    discipline.type === DisciplineType.MandatoryContent;
  const mandatoryContent =
    disciplineTypes.find(isMandatoryContent) ?? EMPTY_MANDATORY_CONTENT;
  const attendedDisciplineTypes = disciplineTypes.filter(
    (discipline) =>
      !isMandatoryContent(discipline) &&
      discipline.attendableLessonsCount > 0 &&
      KNOWN_DISCIPLINE_TYPES.has(discipline.type),
  );

  return {
    isLoading,
    canReportAbsence,
    activeApplications,
    statistics,
    mandatoryContent,
    attendedDisciplineTypes,
    excuses,
    openAbsenceApplication,
    openAbsenceHistory,
    onOpenDetail,
  };
};
